import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  KeycloakAdminApi,
  UserRepresentation,
  UserSearchFilter,
} from '../../services/keycloak-admin-api';
import { RouterLink } from '@angular/router';
import { SidebarService } from '../../services/sidebar.service';

import { debounceTime, distinctUntilChanged, merge } from 'rxjs';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, DatePipe],
  templateUrl: './users-list.html',
  styleUrl: './users-list.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UsersList implements OnInit {
  private keycloakApi = inject(KeycloakAdminApi);
  private fb = inject(FormBuilder);
  private sidebarService = inject(SidebarService);

  users: UserRepresentation[] = [];
  isLoading = false;
  errorMessage: string | null = null;
  searchControl = new FormControl('');

  // Advanced per-field filters; when any is set they take precedence over the broad search box
  filtersForm = this.fb.group({
    username: [''],
    email: [''],
    firstName: [''],
    lastName: [''],
    exact: [false],
  });
  showAdvancedFilters = signal(false);
  // Tracks filtersForm value changes immediately (not debounced) so filter chips update as-you-type
  private filtersFormValue = toSignal(this.filtersForm.valueChanges, {
    initialValue: this.filtersForm.value,
  });

  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalUsers = 0;
  pageSizeOptions = [10, 20, 50, 100];

  constructor() {
    merge(this.searchControl.valueChanges, this.filtersForm.valueChanges)
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => {
        this.currentPage = 1;
        this.loadUsers(this.buildFilter());
      });
  }

  ngOnInit() {
    this.loadUsers(this.buildFilter());
  }

  // Advanced filters take precedence over the broad search box when any field is filled
  private buildFilter(): UserSearchFilter | undefined {
    const { username, email, firstName, lastName, exact } = this.filtersForm.value;
    const hasAdvancedFilter = !!(username || email || firstName || lastName);

    if (hasAdvancedFilter) {
      return {
        ...(username ? { username } : {}),
        ...(email ? { email } : {}),
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
        ...(exact ? { exact: true } : {}),
      };
    }

    const search = this.searchControl.value;
    return search ? { search } : undefined;
  }

  loadUsers(filter?: UserSearchFilter) {
    this.isLoading = true;
    this.errorMessage = null;

    const first = (this.currentPage - 1) * this.pageSize;

    // Load both users and count in parallel
    Promise.all([
      this.keycloakApi.getUsers(filter, first, this.pageSize),
      this.keycloakApi.getUsersCount(filter),
    ])
      .then(([users, count]) => {
        this.users = users;
        this.totalUsers = count;
        this.isLoading = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load users: ${error}`;
        this.isLoading = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  refreshUsers() {
    this.loadUsers(this.buildFilter());
  }

  onPageChange(event: Event) {
    const customEvent = event as CustomEvent;
    this.currentPage = customEvent?.detail?.currentPage ?? 1;
    this.loadUsers(this.buildFilter());
  }

  onPageSizeChange(event: Event) {
    const customEvent = event as CustomEvent;
    this.pageSize = customEvent?.detail?.pageSize ?? 20;
    this.currentPage = 1; // Reset to first page when changing page size
    this.loadUsers(this.buildFilter());
  }

  totalPages = computed(() => {
    return Math.ceil(this.totalUsers / this.pageSize);
  });

  startIndex = computed(() => {
    return (this.currentPage - 1) * this.pageSize + 1;
  });

  endIndex = computed(() => {
    const calculated = this.currentPage * this.pageSize;
    return Math.min(calculated, this.totalUsers);
  });
  // Format display name from user properties
  formatDisplayName(user: UserRepresentation): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.lastName) {
      return user.lastName;
    } else {
      return ' - ';
    }
  }

  // Check if user is from an external identity provider
  isExternalUser(user: UserRepresentation): boolean {
    return !!user.federationLink;
  }

  // Open user edit in sidebar
  openUserEdit(user: UserRepresentation, event: Event): void {
    event.preventDefault(); // Prevent default routing
    event.stopPropagation();
    if (user.id) {
      this.sidebarService.openUserEdit(user.id, user);
    }
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  toggleAdvancedFilters() {
    this.showAdvancedFilters.update((value) => !value);
  }

  clearFilters() {
    this.filtersForm.reset({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      exact: false,
    });
  }

  // Whether at least one advanced field is filled, used to disable the broad search box
  hasAdvancedFilter(): boolean {
    const { username, email, firstName, lastName } = this.filtersForm.value;
    return !!(username || email || firstName || lastName);
  }

  // Chips summarizing active advanced filters, shown when the panel is collapsed so filtering
  // still driving the results isn't hidden from view
  private static readonly FILTER_FIELD_LABELS: Record<string, string> = {
    username: 'Username',
    email: 'Email',
    firstName: 'First name',
    lastName: 'Last name',
  };

  activeFilterChips = computed(() => {
    const { username, email, firstName, lastName } = this.filtersFormValue();
    const fields: [string, string | null | undefined][] = [
      ['username', username],
      ['email', email],
      ['firstName', firstName],
      ['lastName', lastName],
    ];
    return fields
      .filter(([, value]) => !!value)
      .map(([key, value]) => ({
        key,
        label: UsersList.FILTER_FIELD_LABELS[key],
        value: value as string,
      }));
  });

  // Reopen the advanced filters panel, e.g. from the collapsed filter chips
  showFiltersPanel() {
    this.showAdvancedFilters.set(true);
  }

  // Remove a single advanced filter field without reopening the panel
  removeFilterField(key: string) {
    this.filtersForm.patchValue({ [key]: '' });
  }
}
